import { useCallback, useEffect, useMemo, useRef, useState, type ChangeEvent } from "react";
import { PhotoViewer } from "./PhotoViewer";
import { invoke } from "./tauri-web";
import { listen } from "./tauri-web";
import { platform } from "./tauri-web";
import { openFileDialog, saveFileDialog } from "./tauri-web";
import { writeTextFile } from "./tauri-web";
import { supabase } from "./supabase";
import { AiConsent, AI_CONSENT_KEY, signInIos, signInMac, openAccountDelete } from "./ios-native-kit";
import { AccountMenu } from "./AccountMenu";
import { PotMenu, AskPot } from "./PotMenu";
import {
  store, STATUSES, statusLabel, statusColor, quickStats, statusModelCounts, wipAgeDays, nextUpRanked,
  COLOR_FAMILIES, PAINT_TYPES, todayLocal,
  type Faction, type Unit, type Recipe, type Paint, type Status, type CollectionSignals,
} from "./store";
import { recapCollection, openWebLogin, BrainError, type BrainErrorCode } from "./brain";
import { OrioraBadge } from "./OrioraBadge";
import { BackupMenu } from "./BackupMenu";
import { buildExport } from "./export";
import Chat from "./chat/Chat";
import {
  Sparkles, Boxes, Trash2, X, WifiOff, RefreshCw, Plus, Palette,
  FlaskConical, Shield, Pencil, ChevronRight, Flame, GripVertical, Library as LibraryIcon,
  ShoppingCart, Check,
} from "lucide-react";

// SCREENSHOT=0 — sim-only auth bypass for identity screenshots. MUST stay 0 in committed/shipped code.
const SCREENSHOT = 0;

export default function App() {
  const [mounted, setMounted] = useState(false);
  const [authed, setAuthed] = useState(false);
  const [email, setEmail] = useState<string | null>(null);
  // AI-consent gate (APP-READY-STANDARD #62, Apple Guideline 5.1.1(i)). Shown ONCE on first
  // launch before anything else — names the data sent + the AI vendors. Persisted in localStorage.
  const [consented, setConsented] = useState<boolean>(() => {
    try { return localStorage.getItem(AI_CONSENT_KEY) === "1"; } catch { return false; }
  });

  // A deleted/revoked account keeps a locally-valid (unexpired) JWT, so getSession() still
  // returns it and the app looks signed-in. getUser() asks the SERVER — on a real auth
  // rejection (401/403) we purge the dead session and drop to login. Network errors (no
  // status) are ignored so an offline-but-valid user is never signed out.
  const revalidate = useCallback(async () => {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) return;
      const { error } = await supabase.auth.getUser();
      const status = (error as { status?: number } | null)?.status;
      if (error && (status === 401 || status === 403)) {
        try { await supabase.auth.signOut({ scope: "local" }); } catch { /* ignore */ }
        setEmail(null); setAuthed(false);
      }
    } catch { /* network/unknown → keep session (offline-tolerant) */ }
  }, []);

  useEffect(() => {
    // onAuthStateChange fires INITIAL_SESSION after async filesystem storage loads —
    // avoids the cold-launch race where getSession() returned null before storage was ready.
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_e, session) => {
      setAuthed(session !== null);
      setEmail(session?.user?.email ?? null);
      setMounted(true);
      if (_e === 'INITIAL_SESSION' && session) void revalidate();
    });
    const timeout = setTimeout(() => setMounted(true), 3000);
    return () => { clearTimeout(timeout); subscription.unsubscribe(); };
  }, [revalidate]);

  // Re-validate whenever the app returns to the foreground (catches "deleted my account,
  // came back, still signed in").
  useEffect(() => {
    const recheck = () => { if (document.visibilityState === "visible") void revalidate(); };
    document.addEventListener("visibilitychange", recheck);
    window.addEventListener("focus", recheck);
    return () => { document.removeEventListener("visibilitychange", recheck); window.removeEventListener("focus", recheck); };
  }, [revalidate]);

  // Deep-link auth: web login returns oriora-muster://auth#access_token=…&refresh_token=…
  useEffect(() => {
    let unlisten: (() => void) | null = null;
    listen<string>("auth:deep-link", async (e) => {
      try {
        const u = new URL(e.payload);
        const frag = new URLSearchParams(u.hash.replace(/^#/, ""));
        const access_token = frag.get("access_token");
        const refresh_token = frag.get("refresh_token");
        if (access_token && refresh_token) await supabase.auth.setSession({ access_token, refresh_token });
      } catch (err) {
        console.error("deep-link auth failed", err);
      }
    }).then((u) => { unlisten = u; });
    return () => { if (unlisten) unlisten(); };
  }, []);

  // Account-deletion handback: the in-app delete window navigated to oriora-muster://deleted.
  useEffect(() => {
    let unlisten: (() => void) | null = null;
    listen("account:deleted", async () => {
      try { await supabase.auth.signOut({ scope: "local" }); } catch { /* ignore */ }
      setEmail(null); setAuthed(false);
    }).then((u) => { unlisten = u; });
    return () => { if (unlisten) unlisten(); };
  }, []);

  // First-launch AI-consent gate — before login + before any AI feature is reachable.
  if (!consented && !SCREENSHOT) {
    return <AiConsent brand="Muster"
      onAccept={() => { try { localStorage.setItem(AI_CONSENT_KEY, "1"); } catch { /* still consented this session */ } setConsented(true); }} />;
  }
  if (!mounted && !SCREENSHOT) return <div style={{ height: "100%", display: "grid", placeItems: "center" }} className="muted">Loading…</div>;
  if (!authed && !SCREENSHOT) return <Login />;
  return <Home email={SCREENSHOT ? "you@oriora.app" : email} onSignedOut={() => { setEmail(null); setAuthed(false); }} />;
}

function Login() {
  const [busy, setBusy] = useState(false);
  return (
    <main style={{ height: "100dvh", display: "flex", flexDirection: "column", overflowY: "auto", WebkitOverflowScrolling: "touch" as any, boxSizing: "border-box", padding: "max(1.5rem, env(safe-area-inset-top)) 1.5rem max(1.5rem, env(safe-area-inset-bottom))" }}>
      <div className="card" style={{ width: "100%", maxWidth: 380, padding: "2rem", textAlign: "center", margin: "auto" }}>
        <div style={{ display: "inline-flex", marginBottom: "0.4rem", color: "var(--accent)" }}><Boxes size={40} /></div>
        <div style={{ color: "var(--accent)", fontWeight: 700, fontSize: "var(--fs-sm)" }}>Muster</div>
        <h1 className="serif" style={{ fontSize: "1.5rem", margin: "0.4rem 0 0.4rem" }}>Your painting queue, mustered</h1>
        <p className="muted" style={{ fontSize: "var(--fs-xs)", lineHeight: 1.5, marginBottom: "1.25rem" }}>
          Uses your Oriora account to sign in, then returns here. Your collection lives on this
          device; the collection recap runs through your Oriora account. Oriora doesn't retain what you send for an AI action. You can also back up to your own GitHub or Google Drive.
        </p>
        <button className="btn btn-primary" style={{ width: "100%" }} disabled={busy}
          onClick={async () => {
            setBusy(true);
            // iOS → in-app ASWebAuthenticationSession (Apple Guideline 4, #61); desktop → in-app login window.
            try { const p = platform(); if (p === "ios") await signInIos("oriora-muster"); else if (p === "macos") await signInMac("oriora-muster"); else if (p === "web") window.location.href = "/login?next=/oriora-muster/"; else await openWebLogin(); }
            catch { /* user dismissed the sheet or it failed — reset and let them retry */ }
            finally { setBusy(false); }
          }}>
          {busy ? "Signing in…" : "Sign in with Oriora"}
        </button>
      </div>
    </main>
  );
}

function Home({ email, onSignedOut }: { email: string | null; onSignedOut: () => void }) {
  const [factions, setFactions] = useState<Faction[]>([]);
  const [units, setUnits] = useState<Unit[]>([]);
  const [recipes, setRecipes] = useState<Recipe[]>([]);
  const [paints, setPaints] = useState<Paint[]>([]);
  const [offline, setOffline] = useState(typeof navigator !== "undefined" && navigator.onLine === false);
  // Army filter — the board's own filter rail. "" = every army. (Recipes/paints/armies now hang
  // OFF the board via the Library, not as stacked h2 sections — that section shape was the
  // shared fleet skeleton a 4.3(a) reviewer reads as template-spam.)
  const [filterFaction, setFilterFaction] = useState<string>("");

  // Editor modals
  const [unitModal, setUnitModal] = useState<{ editing: Unit | null } | null>(null);
  const [factionModal, setFactionModal] = useState<{ editing: Faction | null } | null>(null);
  const [recipeModal, setRecipeModal] = useState<{ editing: Recipe | null } | null>(null);
  const [paintModal, setPaintModal] = useState<{ editing: Paint | null } | null>(null);
  const [confirm, setConfirm] = useState<{ label: string; detail: string; onYes: () => void } | null>(null);
  // Overlays that hang OFF the board — the collection Library and the ONE billed AI recap. Recap
  // is now a single affordance on the board's chrome ("Read my pile back"), not a top-level tab.
  const [libraryOpen, setLibraryOpen] = useState(false);
  const [recapOpen, setRecapOpen] = useState(false);
  // Chat panel (ADDITIVE): a natural-language front door over the SAME capabilities — no existing screen
  // is altered. See src/chat/{brain,actions,Chat}.tsx.
  const [chatOpen, setChatOpen] = useState(false);

  useEffect(() => {
    const goOnline = () => setOffline(false);
    const goOffline = () => setOffline(true);
    window.addEventListener("online", goOnline);
    window.addEventListener("offline", goOffline);
    return () => { window.removeEventListener("online", goOnline); window.removeEventListener("offline", goOffline); };
  }, []);

  const reload = useCallback(async () => {
    const [f, u, r, p] = await Promise.all([
      store.listFactions(), store.listUnits(), store.listRecipes(), store.listPaints(),
    ]);
    setFactions(f); setUnits(u); setRecipes(r); setPaints(p);
  }, []);
  useEffect(() => { void reload(); }, [reload]);

  const doExport = useCallback(() => {
    if (units.length === 0) return;
    const { csv, filename } = buildExport(units, factions, recipes);
    void (async () => { const path = await saveFileDialog({ defaultPath: filename }); if (path) await writeTextFile(path, csv); })();
  }, [units, factions, recipes]);
  const exportRef = useRef<(() => void) | null>(null);
  exportRef.current = doExport;

  // Native-menu actions (macOS Actions menu): Add unit / Recap / Export.
  useEffect(() => {
    let unlisten: (() => void) | null = null;
    listen<string>("menu:action", (e) => {
      switch (e.payload) {
        case "add_unit": setUnitModal({ editing: null }); break;
        case "recap": setRecapOpen(true); break;
        case "export": exportRef.current?.(); break;
      }
    }).then((u) => { unlisten = u; });
    return () => { if (unlisten) unlisten(); };
  }, []);

  const factionName = useMemo(() => new Map(factions.map((f) => [f.id, f.name])), [factions]);
  const recipeName = useMemo(() => new Map(recipes.map((r) => [r.id, r.name])), [recipes]);

  // The board + summary numerals respect the army filter.
  const visibleUnits = useMemo(
    () => (filterFaction ? units.filter((u) => u.faction_id === filterFaction) : units),
    [units, filterFaction],
  );
  const stats = useMemo(() => quickStats(visibleUnits), [visibleUnits]);
  const nextUp = useMemo(() => nextUpRanked(visibleUnits), [visibleUnits]);

  async function deleteUnit(u: Unit) { await store.deleteUnit(u.id); await reload(); }
  // Direct-manipulation stage change (drag/drop between columns). The Rust `update_unit` stamps
  // wip_since entering WIP and clears it leaving — so staleness stays correct on any transition.
  async function moveUnit(u: Unit, status: Status) {
    if (u.status === status) return;
    await store.updateUnit(u.id, { status });
    await reload();
  }
  async function deleteFaction(f: Faction) { if (filterFaction === f.id) setFilterFaction(""); await store.deleteFaction(f.id); await reload(); }
  async function deleteRecipe(r: Recipe) { await store.deleteRecipe(r.id); await reload(); }
  async function deletePaint(p: Paint) { await store.deletePaint(p.id); await reload(); }
  // "Got it" — one tap moves a shopping-list paint onto the owned shelf (mirrors the fleet's
  // one-gesture state changes). qty stays whatever it was, min 1.
  async function markPaintOwned(p: Paint) { await store.updatePaint(p.id, { wanted: 0, qty: Math.max(1, p.qty) }); await reload(); }

  const empty = factions.length === 0 && units.length === 0;
  const addLabel = factions.length === 0 ? "Add army" : "Add unit";
  const onAdd = () => (factions.length === 0 ? setFactionModal({ editing: null }) : setUnitModal({ editing: null }));

  return (
    <main className="mst-app">
      {offline && (
        <div role="status" className="row mst-offline">
          <WifiOff size={14} style={{ flexShrink: 0 }} /> Recap paused — your pile still saves locally
        </div>
      )}

      {/* 🔴 IDENTITY (2026-08-30): there is NO wordmark header row. The POINTS-PROGRESS figure is
          the header — in this hobby the number ("X of Y pts painted") IS the identity, not the
          app's own name. The two round menu buttons collapsed into ONE paint-pot control
          (PotMenu). Do not reintroduce a wordmark strip, and do not split it back into two
          circles. */}
      <header className="mst-header">
        <PotMenu
          backup={
            <BackupMenu
              appName="Muster" fileSlug="muster" appScheme="oriora-muster"
              hasData={units.length > 0} onRestored={reload}
              onExportCsv={units.length > 0 ? doExport : undefined}
            />
          }
          account={<AccountMenu email={email} appSlug="oriora-muster" appName="Oriora Muster" onSignedOut={onSignedOut} />}
        />
      </header>

      {empty ? (
        <>
        {/* READY-STATE 2026-09-19 — the empty board used to be the fleet's shared card (shield
            glyph → serif line → two grey lines → one pill) whose only door was "Add army", i.e. a
            blank form that produces an EMPTY army and still no pile. It now leads with the thing
            the ledger claims as this app's header and PARITY-MATRIX rows 5+6 call the difference:
            the points-progress read and the five-stage pipeline, drawn empty and labelled, with a
            one-line door that puts a real unit in it. The full army form is kept one line below. */}
        <StartPile
          onAdded={reload}
          onAddArmy={() => setFactionModal({ editing: null })}
        />
        <AskPot onAsk={() => setChatOpen(true)} />
        </>
      ) : (
        <>
          {/* Summary rail carries the painted-vs-total headline + the recap/library actions —
              the read-only stat HERO is gone; per-stage numerals live on the board columns. */}
          <SummaryRail
            stats={stats} visibleUnits={visibleUnits}
            addLabel={addLabel} onAdd={onAdd}
            canRecap={units.length > 0} onRecap={() => setRecapOpen(true)}
            onLibrary={() => setLibraryOpen(true)}
            onAsk={() => setChatOpen(true)}
          />

          {/* Filter rail — armies as chips hanging off the board. */}
          {factions.length > 0 && (
            <div className="mst-filter" role="group" aria-label="Filter by army">
              <button className={`chip mst-filter__chip${filterFaction === "" ? " selected" : ""}`} onClick={() => setFilterFaction("")}>All armies</button>
              {factions.map((f) => (
                <button key={f.id} className={`chip mst-filter__chip${filterFaction === f.id ? " selected" : ""}`} onClick={() => setFilterFaction(f.id)}>{f.name}</button>
              ))}
            </div>
          )}

          {/* Focus slot — "pick up next", the signal the board is built around. */}
          <FocusSlot
            unit={nextUp[0] ?? null} factionName={factionName}
            hasUnits={visibleUnits.length > 0}
            onOpen={(u) => setUnitModal({ editing: u })}
          />

          {visibleUnits.length === 0 ? (
            <div className="muted mst-board-empty">No units in this view yet — use Add unit to log one.</div>
          ) : (
            <Board
              units={visibleUnits} factionName={factionName} recipeName={recipeName}
              onEdit={(u) => setUnitModal({ editing: u })}
              onDelete={(u) => setConfirm({ label: "Delete unit?", detail: `Remove “${u.name}”? This can’t be undone.`, onYes: () => { setConfirm(null); void deleteUnit(u); } })}
              onMove={(u, s) => void moveUnit(u, s)}
            />
          )}
        </>
      )}

      {libraryOpen && (
        <Library
          factions={factions} units={units} recipes={recipes} paints={paints}
          onClose={() => setLibraryOpen(false)}
          onAddFaction={() => setFactionModal({ editing: null })}
          onEditFaction={(f) => setFactionModal({ editing: f })}
          onDeleteFaction={(f) => setConfirm({ label: "Delete army?", detail: `Remove “${f.name}” and all its units? This can’t be undone.`, onYes: () => { setConfirm(null); void deleteFaction(f); } })}
          onAddRecipe={() => setRecipeModal({ editing: null })}
          onEditRecipe={(r) => setRecipeModal({ editing: r })}
          onDeleteRecipe={(r) => setConfirm({ label: "Delete recipe?", detail: `Remove “${r.name}”? Units using it keep their other details.`, onYes: () => { setConfirm(null); void deleteRecipe(r); } })}
          onAddPaint={() => setPaintModal({ editing: null })}
          onEditPaint={(p) => setPaintModal({ editing: p })}
          onDeletePaint={(p) => setConfirm({ label: "Delete paint?", detail: `Remove “${p.name}” from your ${p.wanted ? "shopping list" : "inventory"}?`, onYes: () => { setConfirm(null); void deletePaint(p); } })}
          onMarkPaintOwned={(p) => void markPaintOwned(p)}
        />
      )}

      {recapOpen && (
        <ModalShell title="Read my pile back to me" onCancel={() => setRecapOpen(false)} wide>
          <RecapView units={units} stats={quickStats(units)} bare />
        </ModalShell>
      )}

      {unitModal && (
        <UnitEditor editing={unitModal.editing} factions={factions} recipes={recipes}
          onClose={() => setUnitModal(null)} onSaved={() => { setUnitModal(null); void reload(); }} />
      )}
      {factionModal && (
        <FactionEditor editing={factionModal.editing}
          onClose={() => setFactionModal(null)} onSaved={() => { setFactionModal(null); void reload(); }} />
      )}
      {recipeModal && (
        <RecipeEditor editing={recipeModal.editing}
          onClose={() => setRecipeModal(null)} onSaved={() => { setRecipeModal(null); void reload(); }} />
      )}
      {paintModal && (
        <PaintEditor editing={paintModal.editing}
          onClose={() => setPaintModal(null)} onSaved={() => { setPaintModal(null); void reload(); }} />
      )}
      {confirm && (
        <ModalShell title={confirm.label} onCancel={() => setConfirm(null)}>
          <p className="muted" style={{ fontSize: "var(--fs-xs)", margin: "0 0 1rem" }}>{confirm.detail}</p>
          <div className="row" style={{ justifyContent: "flex-end", gap: "0.4rem" }}>
            <button className="btn" style={{ fontSize: "var(--fs-xs)" }} onClick={() => setConfirm(null)}>Cancel</button>
            <button className="btn btn-primary" style={{ fontSize: "var(--fs-xs)", background: "var(--rose)", borderColor: "var(--rose)" }} onClick={confirm.onYes}>Delete</button>
          </div>
        </ModalShell>
      )}

      {/* Chat panel — a natural-language way to use the app by typing. Purely additive: the launcher
          + slide-over live on top; no existing screen is altered. A chat-driven change reloads the collection. */}
      {/* 🔴 IDENTITY (2026-08-30): the floating "Chat" pill that used to launch this was DELETED —
        same pill, same corner, same label in ~15 sibling apps. The assistant is NOT removed and
        must never be: its entry is the .mst-pot paint pot on the army rail, which calls this same
        setChatOpen. If a future change moves it, REPLACE it — never drop it. */}
      <aside className={`mst-sheet ${chatOpen ? "open" : ""}`} aria-hidden={!chatOpen}>
        <div className="mst-head">
          <span className="row" style={{ gap: "0.35rem" }}><Sparkles size={14} /> Assistant — type to add, mark, find, or recap</span>
          <button className="btn-ghost" onClick={() => setChatOpen(false)}>Close ↓</button>
        </div>
        <Chat onChanged={reload} />
      </aside>
    </main>
  );
}

// ── READY-STATE 2026-09-19 — the FIRST screen, zero units ───────────────────────────────────
// paintRack (the leader) has no backlog at all — it is a paint library over a closed 29k catalog.
// The pile read (points painted vs not, across unbuilt → built → primed → WIP → painted) is
// PARITY-MATRIX row 5, and the ledger names the points figure as this app's header. Both were
// invisible until the user had already built an army and logged units through two separate forms.
// Here the pipeline is drawn before there is any data — empty compartments, real stage colours,
// real labels — and ONE line puts a real unit into it: name, points, stage. The army is created
// with it (named from a tapped system chip, or "My collection"), so nobody has to make an empty
// army first. Everything is local: no catalog, no lookup, no AI call.
const START_SYSTEMS = ["Warhammer 40,000", "Age of Sigmar", "Kill Team", "Bolt Action", "Middle-earth SBG"];

function StartPile({ onAdded, onAddArmy }: { onAdded: () => Promise<void> | void; onAddArmy: () => void }) {
  const [army, setArmy] = useState("");
  const [name, setName] = useState("");
  const [pts, setPts] = useState("");
  const [stage, setStage] = useState<Status>("unbuilt");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  async function add() {
    const n = name.trim();
    if (!n) { setErr("Give the unit a name — everything else can change later."); return; }
    setBusy(true); setErr(null);
    try {
      const systemName = army.trim();
      const faction = await store.createFaction({
        name: systemName || "My collection",
        game_system: systemName,
      });
      await store.createUnit({
        faction_id: faction.id, name: n,
        points: Number(pts.replace(/[^0-9]/g, "")) || 0,
        model_count: 1, status: stage,
      });
      await onAdded();
    } catch (e) {
      setErr(`Couldn't add that — ${e instanceof Error ? e.message : String(e)}`);
      setBusy(false);
    }
  }

  return (
    <div className="mst-start">
      <div className="serif tnum mst-start__pts">
        0<span className="mst-start__ptsdim"> / 0 pts painted</span>
      </div>
      <div className="mst-start__lede">
        The pile, in points. Every unit sits in one of five stages — this bar is the answer to
        “how much of my army is actually finished?”
      </div>

      {/* The pipeline, drawn before there is data: the vocabulary taught at a glance. */}
      <div className="mst-start__pipe" aria-hidden>
        {STATUSES.map((s) => (
          <div key={s} className="mst-start__cell">
            <span className="mst-start__swatch" style={{ background: statusColor(s) }} />
            <span className="mst-start__cellLbl">{statusLabel(s)}</span>
          </div>
        ))}
      </div>

      <div className="mst-start__rule"><span>Put something in it</span></div>

      <div className="mst-start__chips" role="group" aria-label="Game system (optional)">
        {START_SYSTEMS.map((s) => (
          <button key={s} type="button" className={`chip mst-filter__chip${army === s ? " selected" : ""}`}
            onClick={() => setArmy(army === s ? "" : s)}>{s}</button>
        ))}
      </div>

      <div className="mst-start__row">
        <input value={name} onChange={(e) => setName(e.target.value)} aria-label="Unit name"
          placeholder="Unit — e.g. Terminator Squad" className="mst-start__name"
          onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); void add(); } }} />
        <input value={pts} onChange={(e) => setPts(e.target.value)} aria-label="Points"
          inputMode="numeric" placeholder="pts" className="mst-start__pts-in"
          onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); void add(); } }} />
      </div>

      <div className="mst-start__chips" role="group" aria-label="Stage">
        {STATUSES.map((s) => (
          <button key={s} type="button" className={`chip mst-filter__chip${stage === s ? " selected" : ""}`}
            onClick={() => setStage(s)}>{statusLabel(s)}</button>
        ))}
      </div>

      {err && <div className="mst-start__err">{err}</div>}

      <button className="btn btn-primary btn-pill mst-start__go" disabled={busy} onClick={() => void add()}>
        <Plus size={15} /> {busy ? "Adding…" : "Add to the pile"}
      </button>

      <div className="mst-start__foot">
        Paints are free text — any brand, any indie line, any custom mix, nothing has to be in a
        catalogue — and the shopping list is free too.
        {" "}
        <button className="btn-ghost mst-start__alt" onClick={onAddArmy}>Set up an army first</button>
      </div>
    </div>
  );
}

// ── Summary rail — the pile-of-shame headline the category leads with (Pile of Potential's
//    % + finished-points pair), carried as a slim rail above the board instead of a stat hero
//    card. Per-stage numerals live on the board columns themselves. ──────────────────────────
function SummaryRail({ stats, visibleUnits, addLabel, onAdd, canRecap, onRecap, onLibrary, onAsk }: {
  stats: ReturnType<typeof quickStats>;
  visibleUnits: Unit[];
  addLabel: string; onAdd: () => void;
  canRecap: boolean; onRecap: () => void; onLibrary: () => void; onAsk: () => void;
}) {
  const modelCounts = useMemo(() => statusModelCounts(visibleUnits), [visibleUnits]);
  const totalModels = stats.models;
  return (
    <section className="mst-rail">
      <div className="mst-rail__top">
        <div className="mst-rail__figure">
          <span className="serif tnum mst-rail__pts">
            {stats.paintedPoints.toLocaleString()}<span className="mst-rail__ptsdim"> / {stats.totalPoints.toLocaleString()} pts painted</span>
          </span>
          <span className="mst-rail__meta tnum">
            {stats.paintedPct === null ? "No points logged yet" : `${stats.paintedPct.toFixed(0)}% of your points painted`}
            {` · ${stats.units} unit${stats.units === 1 ? "" : "s"} · ${stats.models} model${stats.models === 1 ? "" : "s"}`}
            {stats.wipCount > 0 ? ` · ${stats.wipCount} WIP` : ""}
          </span>
        </div>
        <div className="mst-rail__actions">
          <button className="btn btn-primary btn-pill" onClick={onAdd}><Plus size={16} /> {addLabel}</button>
          <button className="btn btn-pill" onClick={onRecap} disabled={!canRecap}><Sparkles size={15} /> Read my pile back</button>
          <button className="btn btn-pill" onClick={onLibrary}><LibraryIcon size={15} /> Library</button>
        </div>
      </div>
      {totalModels > 0 && (
        <div className="mst-rail__bar" aria-hidden>
          <div className="progress-track">
            {STATUSES.map((s) => {
              const c = modelCounts[s];
              if (c === 0) return null;
              return <div key={s} className="progress-seg" title={`${statusLabel(s)}: ${c}`} style={{ width: `${(c / totalModels) * 100}%`, background: statusColor(s) }} />;
            })}
          </div>
        </div>
      )}

      {/* 🔴 THE ASSISTANT ENTRY — a paint pot docked to the army rail, replacing the floating
          "Chat" pill. Rendered on the rail in every state so the AI is always reachable from
          home. onAsk opens the same Chat sheet: moved, never removed. */}
      <AskPot onAsk={onAsk} />
    </section>
  );
}

// ── Focus slot — "pick up next", promoted from a 40px banner to the signal the board is built
//    around. nextUpRanked() (oldest-WIP first, then closest-to-painted) + the WIP day count are
//    the two things no researched competitor surfaces. ────────────────────────────────────────
function FocusSlot({ unit, factionName, hasUnits, onOpen }: {
  unit: Unit | null; factionName: Map<string, string>; hasUnits: boolean; onOpen: (u: Unit) => void;
}) {
  if (!unit) {
    if (!hasUnits) return null;
    return (
      <div className="mst-focus mst-focus--done">
        <div className="mst-focus__col">
          <span className="mst-focus__eyebrow"><Flame size={14} /> Pick up next</span>
          <span className="mst-focus__name">Everything here is painted</span>
          <span className="mst-focus__meta">Nothing left on the pile in this view.</span>
        </div>
      </div>
    );
  }
  const age = unit.status === "wip" ? wipAgeDays(unit.wip_since) : null;
  const fac = factionName.get(unit.faction_id) ?? "";
  return (
    <button className="mst-focus" onClick={() => onOpen(unit)}>
      <div className="mst-focus__col">
        <span className="mst-focus__eyebrow"><Flame size={14} /> Pick up next</span>
        <span className="mst-focus__name">{unit.name}</span>
        <span className="mst-focus__meta tnum">
          {fac ? `${fac} · ` : ""}{statusLabel(unit.status)}
          {age !== null ? ` · sat ${age}d in progress` : ""}
          {` · ${unit.points.toLocaleString()} pts · ${unit.model_count} mdl`}
        </span>
      </div>
      <ChevronRight className="mst-focus__go" size={20} />
    </button>
  );
}

// ── Library — armies, paint recipes, paint inventory. These hang OFF the board (a secondary
//    route reached from the summary rail), not stacked h2 → vertical .card list sections on the
//    home; that repeated section shape is itself the shared fleet skeleton 4.3(a) flags. ───────
function Library(props: {
  factions: Faction[]; units: Unit[]; recipes: Recipe[]; paints: Paint[];
  onClose: () => void;
  onAddFaction: () => void; onEditFaction: (f: Faction) => void; onDeleteFaction: (f: Faction) => void;
  onAddRecipe: () => void; onEditRecipe: (r: Recipe) => void; onDeleteRecipe: (r: Recipe) => void;
  onAddPaint: () => void; onEditPaint: (p: Paint) => void; onDeletePaint: (p: Paint) => void;
  onMarkPaintOwned: (p: Paint) => void;
}) {
  const { factions, units, recipes, paints } = props;
  const owned = paints.filter((p) => !p.wanted);
  const shopping = paints.filter((p) => p.wanted);
  return (
    <ModalShell title="Library" onCancel={props.onClose} wide>
      <SectionHeader icon={<Shield size={15} />} title="Armies"
        action={<button className="btn" style={miniBtn} onClick={props.onAddFaction}><Plus size={13} /> Add</button>} />
      {factions.length === 0 ? (
        <div className="muted" style={{ fontSize: "var(--fs-sm)", padding: "0.25rem 0 1.25rem" }}>No armies yet.</div>
      ) : (
        <div style={{ display: "grid", gap: "0.5rem", marginBottom: "1.5rem" }}>
          {factions.map((f) => {
            const fu = units.filter((u) => u.faction_id === f.id);
            const fp = fu.reduce((a, u) => a + u.points, 0);
            return (
              <div key={f.id} className="card" style={{ padding: "0.7rem 0.9rem", display: "flex", alignItems: "center", gap: "0.6rem" }}>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontWeight: 600, fontSize: "var(--fs-sm)" }}>{f.name}</div>
                  <div className="muted tnum" style={{ fontSize: "var(--fs-xs)" }}>
                    {f.game_system ? `${f.game_system} · ` : ""}{fu.length} unit{fu.length === 1 ? "" : "s"} · {fp.toLocaleString()} pts
                  </div>
                </div>
                <button className="btn" style={iconBtn} title="Edit army" onClick={() => props.onEditFaction(f)}><Pencil size={14} /></button>
                <button className="btn" style={iconBtn} title="Delete army" onClick={() => props.onDeleteFaction(f)}><Trash2 size={14} /></button>
              </div>
            );
          })}
        </div>
      )}

      <SectionHeader icon={<FlaskConical size={15} />} title="Paint recipes"
        action={<button className="btn" style={miniBtn} onClick={props.onAddRecipe}><Plus size={13} /> Add</button>} />
      {recipes.length === 0 ? (
        <div className="muted" style={{ fontSize: "var(--fs-sm)", padding: "0.25rem 0 1.25rem" }}>
          No recipes yet. A recipe is an ordered list of paint steps you can attach to units.
        </div>
      ) : (
        <div style={{ display: "grid", gap: "0.5rem", marginBottom: "1.5rem" }}>
          {recipes.map((r) => {
            const usedBy = units.filter((u) => u.recipe_id === r.id).length;
            return (
              <div key={r.id} className="card" style={{ padding: "0.7rem 0.9rem", display: "flex", alignItems: "center", gap: "0.6rem" }}>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontWeight: 600, fontSize: "var(--fs-sm)" }}>{r.name}</div>
                  <div className="muted tnum" style={{ fontSize: "var(--fs-xs)" }}>
                    {r.steps?.length ?? 0} step{(r.steps?.length ?? 0) === 1 ? "" : "s"} · on {usedBy} unit{usedBy === 1 ? "" : "s"}
                  </div>
                </div>
                <button className="btn" style={iconBtn} title="Edit recipe" onClick={() => props.onEditRecipe(r)}><Pencil size={14} /></button>
                <button className="btn" style={iconBtn} title="Delete recipe" onClick={() => props.onDeleteRecipe(r)}><Trash2 size={14} /></button>
              </div>
            );
          })}
        </div>
      )}

      <SectionHeader icon={<Palette size={15} />} title="Paint inventory"
        action={<button className="btn" style={miniBtn} onClick={props.onAddPaint}><Plus size={13} /> Add</button>} />
      {owned.length === 0 ? (
        <div className="muted" style={{ fontSize: "var(--fs-sm)", padding: "0.25rem 0 0.25rem" }}>
          No paints logged yet. Track what you own — any brand, custom mix, color family, bottles.
        </div>
      ) : (
        <div style={{ display: "grid", gap: "0.5rem" }}>
          {owned.map((p) => (
            <PaintRow key={p.id} p={p} onEdit={props.onEditPaint} onDelete={props.onDeletePaint} />
          ))}
        </div>
      )}

      {/* Shopping list — paints you still need to buy. A named leader feature (paintRack
          paywalls its "Wish List"); here it is free and on-device, and — because paints are
          free-text, not gate-kept by a remote catalog — ANY paint can go on it. */}
      <div style={{ height: "1.5rem" }} />
      <SectionHeader icon={<ShoppingCart size={15} />} title="Shopping list"
        action={<button className="btn" style={miniBtn} onClick={props.onAddPaint}><Plus size={13} /> Add</button>} />
      {shopping.length === 0 ? (
        <div className="muted" style={{ fontSize: "var(--fs-sm)", padding: "0.25rem 0 0.25rem" }}>
          Nothing to buy. Add a paint here (or edit one) and mark it “On my shopping list” so you
          never forget which colour you still need — or buy a duplicate by mistake.
        </div>
      ) : (
        <div style={{ display: "grid", gap: "0.5rem" }}>
          {shopping.map((p) => (
            <PaintRow key={p.id} p={p} onEdit={props.onEditPaint} onDelete={props.onDeletePaint}
              onGotIt={props.onMarkPaintOwned} />
          ))}
        </div>
      )}
    </ModalShell>
  );
}

// One paint row — used by both the owned shelf and the shopping list. On the shopping list a
// one-tap "Got it" moves the paint onto the owned shelf (`onGotIt`); the owned shelf shows the ×qty.
function PaintRow({ p, onEdit, onDelete, onGotIt }: {
  p: Paint; onEdit: (p: Paint) => void; onDelete: (p: Paint) => void; onGotIt?: (p: Paint) => void;
}) {
  return (
    <div className="card" style={{ padding: "0.6rem 0.9rem", display: "flex", alignItems: "center", gap: "0.6rem" }}>
      <span className="status-dot" style={{ width: 12, height: 12, background: colorSwatch(p.color_family) }} title={p.color_family} />
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontWeight: 600, fontSize: "var(--fs-sm)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{p.name}</div>
        <div className="muted" style={{ fontSize: "var(--fs-xs)" }}>
          {[p.brand, p.paint_type, p.color_family].filter(Boolean).join(" · ")}
        </div>
      </div>
      {onGotIt
        ? <button className="btn" style={{ ...miniBtn, color: "var(--accent)" }} title="Bought it — move to owned" onClick={() => onGotIt(p)}><Check size={13} /> Got it</button>
        : <span className="chip tnum" style={{ flexShrink: 0 }}>×{p.qty}</span>}
      <button className="btn" style={iconBtn} title="Edit paint" onClick={() => onEdit(p)}><Pencil size={14} /></button>
      <button className="btn" style={iconBtn} title="Delete paint" onClick={() => onDelete(p)}><Trash2 size={14} /></button>
    </div>
  );
}

const miniBtn: React.CSSProperties = { fontSize: "var(--fs-xs)", padding: "0.3rem 0.55rem", display: "inline-flex", alignItems: "center", gap: "0.25rem" };
const iconBtn: React.CSSProperties = { padding: "0.3rem 0.4rem", border: "1px solid transparent", background: "none", color: "var(--ink-3)" };

function SectionHeader({ icon, title, action }: { icon: React.ReactNode; title: string; action?: React.ReactNode }) {
  return (
    <div className="row" style={{ justifyContent: "space-between", margin: "0 0 0.6rem" }}>
      <h2 className="row" style={{ fontSize: "1rem", margin: 0, gap: "0.4rem" }}>
        <span style={{ color: "var(--accent)", display: "inline-flex" }}>{icon}</span> {title}
      </h2>
      {action}
    </div>
  );
}

// Compress a picked image to a small on-device JPEG data-URL (never uploaded; the recap only
// ever sees aggregates). Fleet pattern (oriora_closet / oriora_reefline).
function compressImageToDataUrl(file: File, onDone: (dataUrl: string) => void, onErr: () => void) {
  const reader = new FileReader();
  reader.onload = () => {
    const src = reader.result;
    if (typeof src !== "string") { onErr(); return; }
    const img = new Image();
    img.onload = () => {
      const MAX = 1024;
      const scale = Math.min(1, MAX / Math.max(img.width, img.height));
      const w = Math.max(1, Math.round(img.width * scale)), h = Math.max(1, Math.round(img.height * scale));
      const canvas = document.createElement("canvas");
      canvas.width = w; canvas.height = h;
      const ctx = canvas.getContext("2d");
      if (!ctx) { onDone(src); return; }
      ctx.drawImage(img, 0, 0, w, h);
      try { onDone(canvas.toDataURL("image/jpeg", 0.82)); } catch { onDone(src); }
    };
    img.onerror = () => onDone(src);
    img.src = src;
  };
  reader.onerror = onErr;
  reader.readAsDataURL(file);
}

// ── The stage board — Muster's home and its real bones divergence. The five paint-pipeline
//    columns ARE the page (as Liber Pigmenta's Kanban and Pile of Potential's table ARE their
//    homes), full-bleed, not a strip buried under a dashboard. Stage change is DIRECT
//    MANIPULATION: drag a card's grip between columns — Pointer Events, so mouse AND touch (a
//    swipe on iOS) both work — in place of the fleet's per-row "→ next" button. Keyboard / AX
//    users change stage in the unit editor's status picker. ──────────────────────────────────
function Board({ units, factionName, recipeName, onEdit, onDelete, onMove }: {
  units: Unit[];
  factionName: Map<string, string>; recipeName: Map<string, string>;
  onEdit: (u: Unit) => void; onDelete: (u: Unit) => void; onMove: (u: Unit, status: Status) => void;
}) {
  const [dragId, setDragId] = useState<string | null>(null);
  const [overStatus, setOverStatus] = useState<Status | null>(null);
  const [ghost, setGhost] = useState<{ x: number; y: number; label: string } | null>(null);
  const dragRef = useRef<{ id: string } | null>(null);

  const colStatusAt = (x: number, y: number): Status | null => {
    const el = document.elementFromPoint(x, y);
    const col = el && (el as Element).closest?.("[data-col-status]");
    const s = col?.getAttribute("data-col-status");
    return s ? (s as Status) : null;
  };

  const begin = (e: React.PointerEvent, u: Unit) => {
    e.preventDefault();
    e.stopPropagation();
    try { (e.currentTarget as Element).setPointerCapture(e.pointerId); } catch { /* ignore */ }
    dragRef.current = { id: u.id };
    setDragId(u.id);
    setGhost({ x: e.clientX, y: e.clientY, label: u.name });

    const move = (ev: PointerEvent) => {
      setGhost({ x: ev.clientX, y: ev.clientY, label: u.name });
      setOverStatus(colStatusAt(ev.clientX, ev.clientY));
    };
    const finish = (ev: PointerEvent) => {
      window.removeEventListener("pointermove", move);
      window.removeEventListener("pointerup", finish);
      window.removeEventListener("pointercancel", finish);
      const target = colStatusAt(ev.clientX, ev.clientY);
      const active = dragRef.current;
      dragRef.current = null;
      setDragId(null); setOverStatus(null); setGhost(null);
      if (active && target) {
        const moved = units.find((x) => x.id === active.id);
        if (moved && moved.status !== target) onMove(moved, target);
      }
    };
    window.addEventListener("pointermove", move);
    window.addEventListener("pointerup", finish);
    window.addEventListener("pointercancel", finish);
  };

  return (
    <div className="mst-board-wrap">
      <div className="mst-board">
        {STATUSES.map((s) => (
          <KanbanColumn key={s} status={s}
            units={units.filter((u) => u.status === s)}
            isOver={overStatus === s} dragging={dragId !== null} dragId={dragId}
            factionName={factionName} recipeName={recipeName}
            onEdit={onEdit} onDelete={onDelete} onGrip={begin} />
        ))}
      </div>
      {ghost && <div className="mst-ghost" style={{ left: ghost.x, top: ghost.y }}>{ghost.label}</div>}
    </div>
  );
}

function KanbanColumn({ status, units, isOver, dragging, dragId, factionName, recipeName, onEdit, onDelete, onGrip }: {
  status: Status; units: Unit[]; isOver: boolean; dragging: boolean; dragId: string | null;
  factionName: Map<string, string>; recipeName: Map<string, string>;
  onEdit: (u: Unit) => void; onDelete: (u: Unit) => void; onGrip: (e: React.PointerEvent, u: Unit) => void;
}) {
  // Each stage column owns its own numerals (count · models · points) — the metric that used to
  // sit in a hero card above the board now lives on the board itself.
  const models = units.reduce((a, u) => a + u.model_count, 0);
  const points = units.reduce((a, u) => a + u.points, 0);
  return (
    <section className={`mst-col${isOver ? " is-over" : ""}`} data-col-status={status}>
      <header className="mst-col__head">
        <span className="status-dot" style={{ background: statusColor(status) }} />
        <span className="mst-col__title">{statusLabel(status)}</span>
        <span className="mst-col__n tnum">{units.length}</span>
      </header>
      <div className="mst-col__sub tnum">{models} mdl · {points.toLocaleString()} pts</div>
      <div className="mst-col__body" data-col-status={status}>
        {units.length === 0 ? (
          <div className="mst-col__empty">{dragging ? "Drop here" : "—"}</div>
        ) : units.map((u) => (
          <UnitCard key={u.id} u={u} dragging={dragId === u.id}
            factionLabel={factionName.get(u.faction_id) ?? ""}
            recipeLabel={u.recipe_id ? recipeName.get(u.recipe_id) ?? null : null}
            onEdit={() => onEdit(u)} onDelete={() => onDelete(u)} onGrip={(e) => onGrip(e, u)} />
        ))}
      </div>
    </section>
  );
}

function UnitCard({ u, dragging, factionLabel, recipeLabel, onEdit, onDelete, onGrip }: {
  u: Unit; dragging: boolean; factionLabel: string; recipeLabel: string | null;
  onEdit: () => void; onDelete: () => void; onGrip: (e: React.PointerEvent) => void;
}) {
  const [viewing, setViewing] = useState(false);
  const wipAge = u.status === "wip" ? wipAgeDays(u.wip_since) : null;
  const stale = wipAge !== null && wipAge >= 30; // stale-WIP marked on the card itself
  return (
    <div className={`mst-ucard${dragging ? " is-dragging" : ""}${stale ? " is-stale" : ""}`} onClick={onEdit}>
      <button className="mst-grip" aria-label="Drag to another stage" title="Drag to another stage"
        onClick={(e) => e.stopPropagation()} onPointerDown={(e) => onGrip(e)}><GripVertical size={14} /></button>
      <div className="mst-ucard__body">
        <div className="row" style={{ gap: "0.35rem", alignItems: "flex-start", flexWrap: "nowrap" }}>
          {u.photo_ref && (
            <img src={u.photo_ref} alt={u.name} onClick={(ev) => { ev.stopPropagation(); setViewing(true); }}
              style={{ width: 28, height: 28, borderRadius: 6, objectFit: "cover", flexShrink: 0, border: "1px solid var(--border)", cursor: "zoom-in" }} />
          )}
          <div style={{ flex: 1, minWidth: 0 }}>
            <div className="mst-ucard__name">{u.name}</div>
            <div className="muted tnum" style={{ fontSize: 11, marginTop: 2 }}>
              {factionLabel ? `${factionLabel} · ` : ""}{u.points.toLocaleString()}pts · {u.model_count}mdl
              {recipeLabel ? ` · ${recipeLabel}` : ""}
            </div>
          </div>
          <button className="btn mst-ucard__del" title="Delete unit" aria-label="Delete unit"
            onClick={(ev) => { ev.stopPropagation(); onDelete(); }}><Trash2 size={12} /></button>
        </div>
        {stale && <div className="mst-stale"><Flame size={11} /> sat {wipAge}d — finish or move it</div>}
      </div>
      {viewing && u.photo_ref && <PhotoViewer photos={[{ src: u.photo_ref, alt: u.name }]} onClose={() => setViewing(false)} />}
    </div>
  );
}

// ── Recap tab: the ONE billed AI call over derived signals (observational only) ────
type RecapErr = { code: BrainErrorCode | "empty"; message: string };

function RecapView({ units, stats, bare = false }: { units: Unit[]; stats: ReturnType<typeof quickStats>; bare?: boolean }) {
  const [text, setText] = useState<string | null>(null);
  const [signals, setSignals] = useState<CollectionSignals | null>(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<RecapErr | null>(null);

  async function run() {
    setBusy(true); setErr(null); setText(null); setSignals(null);
    try {
      const sig = await store.computeSignals(todayLocal()); // on-device aggregates only
      const r = await recapCollection(sig);                  // pre-LLM fence + ONE billed call
      const recap = r.recap.trim();
      if (!recap) {
        setErr({ code: "empty", message: "Not enough logged yet to recap — add a few more units." });
      } else {
        setText(recap); setSignals(sig);
      }
    } catch (e) {
      if (e instanceof BrainError) setErr({ code: e.code, message: e.message });
      else setErr({ code: "unavailable", message: "Recap failed — please try again." });
    } finally { setBusy(false); }
  }

  if (units.length === 0) {
    return (
      <div className="card" style={{ padding: "2.5rem 1.5rem", textAlign: "center" }}>
        <div style={{ display: "inline-flex", color: "var(--accent)" }}><Sparkles size={40} /></div>
        <div className="serif" style={{ fontSize: "1.2rem", margin: "0.5rem 0 0.25rem" }}>Nothing to recap yet</div>
        <div className="muted" style={{ fontSize: "var(--fs-sm)", maxWidth: 320, margin: "0 auto" }}>
          Log a few units in your Collection, then come back for a recap of your points, recipes, and progress.
        </div>
      </div>
    );
  }

  return (
    <div className={bare ? "" : "card"} style={bare ? undefined : { padding: "1.1rem 1.25rem", borderLeft: "3px solid var(--accent)" }}>
      <div className="row" style={{ justifyContent: "space-between", gap: "0.6rem" }}>
        <div className="row" style={{ fontWeight: 600, fontSize: "var(--fs-sm)", gap: "0.35rem", flexWrap: "wrap" }}>
          <Sparkles size={15} style={{ color: "var(--accent)", flexShrink: 0 }} /> Collection recap
        </div>
        <button className="btn btn-primary row" style={{ fontSize: "var(--fs-xs)", padding: "0.35rem 0.7rem", gap: "0.35rem", flexShrink: 0 }} disabled={busy} onClick={run}>
          {busy ? "Reading…" : text ? (<><RefreshCw size={12} /> Refresh</>) : "Recap my collection"}
        </button>
      </div>

      {!busy && !text && !err && (
        <div className="muted" style={{ fontSize: "var(--fs-xs)", marginTop: 8, lineHeight: 1.6 }}>
          Narrates the figures from your logged collection — points painted vs unpainted ({stats.paintedPoints.toLocaleString()} / {stats.totalPoints.toLocaleString()}),
          your most-used recipes, and how long units have sat WIP. Describes your collection only — no advice.
        </div>
      )}

      {busy && (
        <div aria-live="polite" style={{ marginTop: "0.75rem", display: "grid", gridTemplateColumns: "minmax(0, 1fr)", gap: 8 }}>
          <span className="muted row" style={{ fontSize: "var(--fs-xs)", gap: "0.4rem" }}>
            <Sparkles size={12} style={{ color: "var(--accent)" }} /> Tallying your points, recipes, and WIP ages…
          </span>
          <div className="skeleton-line" style={{ width: "100%" }} />
          <div className="skeleton-line" style={{ width: "92%" }} />
          <div className="skeleton-line" style={{ width: "70%" }} />
        </div>
      )}

      {err && !busy && <RecapError err={err} onRetry={run} />}

      {text && !busy && (
        <div style={{ marginTop: "0.7rem" }} aria-live="polite">
          <p style={{ fontSize: "var(--fs-sm)", color: "var(--ink-2)", lineHeight: 1.65, margin: 0, whiteSpace: "pre-wrap" }}>{text}</p>
          <div style={{ marginTop: 8 }}><OrioraBadge /></div>
          {signals && <SignalEvidence signals={signals} />}
          <div className="row" style={{ marginTop: 10 }}>
            <span className="muted" style={{ fontSize: 10 }}>Describes your logged collection — not advice or recommendations.</span>
          </div>
        </div>
      )}
    </div>
  );
}

function RecapError({ err, onRetry }: { err: RecapErr; onRetry: () => void }) {
  // Insufficient funds — point to the Oriora account on the web, no amounts shown.
  if (err.code === "insufficient_funds") {
    return (
      <div className="row" style={{ marginTop: 10, gap: "0.6rem", alignItems: "flex-start", background: "rgba(192,138,62,0.1)", border: "1px solid rgba(192,138,62,0.3)", borderRadius: 10, padding: "0.6rem 0.75rem" }}>
        <Sparkles size={15} style={{ color: "var(--warning)", flexShrink: 0, marginTop: 1 }} />
        <div>
          <div style={{ fontSize: "var(--fs-xs)", fontWeight: 600 }}>Add funds to keep recapping</div>
          {/* 3.1.1 / 3.1.3(b) anti-steering: an out-of-funds message must NOT name the website —
              pointing the user off-platform to add funds is the exact pattern Keycap, Oracle,
              Closet and Curio were rejected for. Wording matched to Skein, which ships the same
              component and passes. (The account link in iap.tsx is a separate, deliberate thing
              and stays.) */}
          <div className="muted" style={{ fontSize: "var(--fs-xs)", marginTop: 2 }}>Your Oriora balance is too low. Add funds, then recap again.</div>
        </div>
      </div>
    );
  }
  // BYOK gate — Managed AI blocked for this app; needs a vendor key.
  if (err.code === "managed_ai_blocked") {
    return (
      <div className="row" style={{ marginTop: 10, gap: "0.6rem", alignItems: "flex-start", background: "var(--elev-2)", border: "1px solid var(--border)", borderRadius: 10, padding: "0.6rem 0.75rem" }}>
        <Sparkles size={15} style={{ color: "var(--accent)", flexShrink: 0, marginTop: 1 }} />
        <div className="muted" style={{ fontSize: "var(--fs-xs)" }}>{err.message}</div>
      </div>
    );
  }
  // Refused by the on-device observational fence (no billed call was made).
  if (err.code === "refused") {
    return <div className="muted" style={{ fontSize: "var(--fs-xs)", marginTop: 10 }}>{err.message}</div>;
  }
  // Empty result — gentle, no retry (more data is the fix).
  if (err.code === "empty") {
    return <div className="muted" style={{ fontSize: "var(--fs-xs)", marginTop: 10 }}>{err.message}</div>;
  }
  // Network / session / unavailable — recoverable, offer a retry.
  return (
    <div className="row" style={{ marginTop: 10, gap: "0.6rem", alignItems: "center", justifyContent: "space-between" }}>
      <span style={{ fontSize: "var(--fs-xs)", color: "var(--rose)" }}>{err.message}</span>
      {err.code === "unavailable" && (
        <button className="btn row" style={{ fontSize: "var(--fs-xs)", padding: "0.25rem 0.55rem", gap: "0.3rem", flexShrink: 0 }} onClick={onRetry}>
          <RefreshCw size={12} /> Retry
        </button>
      )}
    </div>
  );
}

// The receipts: the actual derived numbers the recap drew from. Purely observational —
// counts, points, frequencies, ages — so the user sees what the AI saw. No advice.
function SignalEvidence({ signals }: { signals: CollectionSignals }) {
  const chips: { label: string; title: string }[] = [];
  const p = signals.points;
  if (p.total > 0) {
    chips.push({ label: `${p.painted.toLocaleString()} / ${p.total.toLocaleString()} pts painted`, title: `${p.painted.toLocaleString()} of ${p.total.toLocaleString()} points painted (${p.painted_pct?.toFixed(0) ?? "0"}%); ${p.unpainted.toLocaleString()} unpainted` });
  }
  const topRecipe = signals.recipes.find((r) => r.units_using > 0);
  if (topRecipe) {
    chips.push({ label: `${topRecipe.name} ·${topRecipe.models_using} models`, title: `Most-used recipe "${topRecipe.name}" (${topRecipe.step_count} steps) on ${topRecipe.units_using} unit(s), ${topRecipe.models_using} model(s)` });
  }
  if (signals.wip.count > 0) {
    const oldest = signals.wip.oldest_days;
    chips.push({ label: oldest !== null ? `oldest WIP ${oldest}d` : `${signals.wip.count} WIP`, title: `${signals.wip.count} unit(s) in progress${oldest !== null ? `; oldest sat ${oldest} days` : ""}${signals.wip.over_year > 0 ? `; ${signals.wip.over_year} over a year` : ""}` });
  }
  if (signals.paints.bottles > 0) {
    const topColor = signals.paints.by_color_family[0];
    chips.push({ label: `${signals.paints.bottles} paints${topColor ? ` · ${topColor.name}` : ""}`, title: `${signals.paints.distinct} distinct paints, ${signals.paints.bottles} bottles${topColor ? `; most-stocked family: ${topColor.name} (${topColor.count})` : ""}` });
  }
  if (signals.factions.length > 1) {
    const top = signals.factions[0];
    chips.push({ label: `${signals.factions.length} armies · top ${top.total_points.toLocaleString()}pts`, title: `${signals.factions.length} armies; largest "${top.name}" at ${top.total_points.toLocaleString()} pts` });
  }

  if (chips.length === 0) return null;
  return (
    <div style={{ display: "flex", flexWrap: "wrap", gap: "0.3rem", marginTop: "0.7rem" }}>
      {chips.map((c) => (
        <span key={c.label} className="chip tnum" title={c.title}
          style={{ padding: "0.1rem 0.45rem", fontSize: 10, background: "var(--accent-soft)", borderColor: "transparent", color: "var(--accent-soft-ink)" }}>
          {c.label}
        </span>
      ))}
    </div>
  );
}

// ── Editors ────────────────────────────────────────────────────────────────────────
function UnitEditor({ editing, factions, recipes, onClose, onSaved }:
  { editing: Unit | null; factions: Faction[]; recipes: Recipe[]; onClose: () => void; onSaved: () => void }) {
  const isEditing = editing !== null;
  const [factionId, setFactionId] = useState(editing?.faction_id ?? factions[0]?.id ?? "");
  const [name, setName] = useState(editing?.name ?? "");
  const [points, setPoints] = useState(String(editing?.points ?? ""));
  const [models, setModels] = useState(String(editing?.model_count ?? "1"));
  const [status, setStatus] = useState<Status>(editing?.status ?? "unbuilt");
  const [recipeId, setRecipeId] = useState<string>(editing?.recipe_id ?? "");
  const [notes, setNotes] = useState(editing?.notes ?? "");
  const [photoRef, setPhotoRef] = useState(editing?.photo_ref ?? "");
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  // Photo of the painted model — compressed data-URL kept on-device (never uploaded).
  const photoInputRef = useRef<HTMLInputElement>(null);
  async function pickPhoto() {
    setErr(null);
    if (platform() === "ios") { photoInputRef.current?.click(); return; }
    try {
      const p = await openFileDialog({ multiple: false, filters: [{ name: "Image", extensions: ["png", "jpg", "jpeg", "gif", "webp", "heic"] }] });
      if (typeof p === "string") setPhotoRef(p);
    } catch { /* cancelled */ }
  }
  function onPhotoInputChange(e: ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    compressImageToDataUrl(file, setPhotoRef, () => setErr("Could not read that photo — try another."));
  }

  async function save() {
    if (!name.trim() || !factionId) return;
    setSaving(true); setErr(null);
    const pts = Math.max(0, parseInt(points || "0", 10) || 0);
    const mdl = Math.max(0, parseInt(models || "1", 10) || 0);
    try {
      if (isEditing) {
        await store.updateUnit(editing!.id, {
          faction_id: factionId, name: name.trim(), points: pts, model_count: mdl, status,
          recipe_id: recipeId || null, notes: notes.trim(), photo_ref: photoRef,
        });
      } else {
        await store.createUnit({
          faction_id: factionId, name: name.trim(), points: pts, model_count: mdl, status,
          recipe_id: recipeId || null, notes: notes.trim(), photo_ref: photoRef,
        });
      }
      onSaved();
    } catch (e) {
      setErr(`Could not save — try again. (${e instanceof Error ? e.message : String(e)})`);
      setSaving(false);
    }
  }

  return (
    <ModalShell title={isEditing ? "Edit unit" : "Add unit"} onCancel={onClose} wide>
      <Field label="Army">
        <select value={factionId} onChange={(e) => setFactionId(e.target.value)}>
          {factions.map((f) => <option key={f.id} value={f.id}>{f.name}</option>)}
        </select>
      </Field>
      <Field label="Unit name">
        <input placeholder="e.g. Intercessor Squad" value={name} onChange={(e) => setName(e.target.value)} />
      </Field>
      <div className="row" style={{ gap: "0.6rem", alignItems: "center", margin: "0 0 0.6rem" }}>
        {photoRef ? (
          <img src={photoRef} alt="" style={{ width: 56, height: 56, borderRadius: 10, objectFit: "cover", flexShrink: 0, border: "1px solid var(--line, rgba(0,0,0,0.12))" }} />
        ) : (
          <div style={{ width: 56, height: 56, borderRadius: 10, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, background: "var(--elev-2, rgba(0,0,0,0.06))", fontSize: 22 }} aria-hidden>🎨</div>
        )}
        <button type="button" className="btn" style={{ fontSize: "var(--fs-xs)", whiteSpace: "nowrap" }} onClick={pickPhoto}>{photoRef ? "Change photo" : "Add photo"}</button>
        {photoRef && <button type="button" className="btn" style={{ fontSize: "var(--fs-xs)", whiteSpace: "nowrap" }} onClick={() => setPhotoRef("")}>Remove</button>}
        <input ref={photoInputRef} type="file" accept="image/*" onChange={onPhotoInputChange} style={{ display: "none" }} />
      </div>
      <div className="row" style={{ gap: "0.6rem", alignItems: "flex-start" }}>
        <div style={{ flex: 1 }}><Field label="Points"><input inputMode="numeric" placeholder="0" value={points} onChange={(e) => setPoints(e.target.value.replace(/[^0-9]/g, ""))} /></Field></div>
        <div style={{ flex: 1 }}><Field label="Models"><input inputMode="numeric" placeholder="1" value={models} onChange={(e) => setModels(e.target.value.replace(/[^0-9]/g, ""))} /></Field></div>
      </div>
      <Field label="Status">
        <div className="status-seg">
          {STATUSES.map((s) => (
            <button key={s} type="button" className={`status-opt${status === s ? " selected" : ""}`} onClick={() => setStatus(s)}>
              <span className="status-dot" style={{ background: statusColor(s) }} /> {statusLabel(s)}
            </button>
          ))}
        </div>
      </Field>
      <Field label="Paint recipe (optional)">
        <select value={recipeId} onChange={(e) => setRecipeId(e.target.value)}>
          <option value="">— none —</option>
          {recipes.map((r) => <option key={r.id} value={r.id}>{r.name}</option>)}
        </select>
      </Field>
      <Field label="Notes (optional)">
        <textarea rows={2} placeholder="Conversions, basing, kill-team, etc." value={notes} onChange={(e) => setNotes(e.target.value)} />
      </Field>
      {err && <div style={{ fontSize: "var(--fs-xs)", color: "var(--rose)", marginBottom: "0.75rem" }}>{err}</div>}
      <ModalActions onCancel={onClose} saving={saving} canSave={!!name.trim() && !!factionId}
        saveLabel={isEditing ? "Update" : "Add unit"} onSave={save} />
    </ModalShell>
  );
}

function FactionEditor({ editing, onClose, onSaved }: { editing: Faction | null; onClose: () => void; onSaved: () => void }) {
  const isEditing = editing !== null;
  const [name, setName] = useState(editing?.name ?? "");
  const [system, setSystem] = useState(editing?.game_system ?? "");
  const [notes, setNotes] = useState(editing?.notes ?? "");
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  async function save() {
    if (!name.trim()) return;
    setSaving(true); setErr(null);
    try {
      if (isEditing) await store.updateFaction(editing!.id, { name: name.trim(), game_system: system.trim(), notes: notes.trim() });
      else await store.createFaction({ name: name.trim(), game_system: system.trim(), notes: notes.trim() });
      onSaved();
    } catch (e) { setErr(`Could not save — try again. (${e instanceof Error ? e.message : String(e)})`); setSaving(false); }
  }

  return (
    <ModalShell title={isEditing ? "Edit army" : "Add army"} onCancel={onClose}>
      <Field label="Army name"><input placeholder="e.g. Ultramarines" value={name} onChange={(e) => setName(e.target.value)} /></Field>
      <Field label="Game system (optional)"><input placeholder="e.g. Warhammer 40k" value={system} onChange={(e) => setSystem(e.target.value)} /></Field>
      <Field label="Notes (optional)"><textarea rows={2} placeholder="List theme, sub-faction, etc." value={notes} onChange={(e) => setNotes(e.target.value)} /></Field>
      {err && <div style={{ fontSize: "var(--fs-xs)", color: "var(--rose)", marginBottom: "0.75rem" }}>{err}</div>}
      <ModalActions onCancel={onClose} saving={saving} canSave={!!name.trim()} saveLabel={isEditing ? "Update" : "Add army"} onSave={save} />
    </ModalShell>
  );
}

function RecipeEditor({ editing, onClose, onSaved }: { editing: Recipe | null; onClose: () => void; onSaved: () => void }) {
  const isEditing = editing !== null;
  const [name, setName] = useState(editing?.name ?? "");
  const [steps, setSteps] = useState<string[]>(editing ? [...editing.steps] : [""]);
  const [notes, setNotes] = useState(editing?.notes ?? "");
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  function setStep(i: number, v: string) { setSteps((s) => s.map((x, idx) => (idx === i ? v : x))); }
  function addStep() { setSteps((s) => [...s, ""]); }
  function removeStep(i: number) { setSteps((s) => (s.length <= 1 ? s : s.filter((_, idx) => idx !== i))); }

  async function save() {
    if (!name.trim()) return;
    setSaving(true); setErr(null);
    const cleaned = steps.map((s) => s.trim()).filter(Boolean);
    try {
      if (isEditing) await store.updateRecipe(editing!.id, { name: name.trim(), steps: cleaned, notes: notes.trim() });
      else await store.createRecipe({ name: name.trim(), steps: cleaned, notes: notes.trim() });
      onSaved();
    } catch (e) { setErr(`Could not save — try again. (${e instanceof Error ? e.message : String(e)})`); setSaving(false); }
  }

  return (
    <ModalShell title={isEditing ? "Edit recipe" : "Add recipe"} onCancel={onClose} wide>
      <Field label="Recipe name"><input placeholder="e.g. 7-step metallic" value={name} onChange={(e) => setName(e.target.value)} /></Field>
      <div style={{ fontWeight: 600, fontSize: "var(--fs-sm)", marginBottom: "0.4rem" }}>Steps <span className="muted" style={{ fontWeight: 400 }}>(in order)</span></div>
      <div style={{ display: "grid", gridTemplateColumns: "minmax(0, 1fr)", gap: "0.4rem", marginBottom: "0.6rem" }}>
        {steps.map((s, i) => (
          <div key={i} className="row" style={{ gap: "0.4rem", flexWrap: "nowrap" }}>
            <span className="tnum muted" style={{ width: 18, textAlign: "right", fontSize: "var(--fs-xs)" }}>{i + 1}.</span>
            <input placeholder={`Step ${i + 1} — e.g. Basecoat Leadbelcher`} value={s} onChange={(e) => setStep(i, e.target.value)} />
            <button type="button" className="btn" style={iconBtn} title="Remove step" onClick={() => removeStep(i)} disabled={steps.length <= 1}><X size={14} /></button>
          </div>
        ))}
      </div>
      <button type="button" className="btn" style={{ ...miniBtn, marginBottom: "1rem" }} onClick={addStep}><Plus size={13} /> Add step</button>
      <Field label="Notes (optional)"><textarea rows={2} placeholder="Thinning, drying, varnish, etc." value={notes} onChange={(e) => setNotes(e.target.value)} /></Field>
      {err && <div style={{ fontSize: "var(--fs-xs)", color: "var(--rose)", marginBottom: "0.75rem" }}>{err}</div>}
      <ModalActions onCancel={onClose} saving={saving} canSave={!!name.trim()} saveLabel={isEditing ? "Update" : "Add recipe"} onSave={save} />
    </ModalShell>
  );
}

function PaintEditor({ editing, onClose, onSaved }: { editing: Paint | null; onClose: () => void; onSaved: () => void }) {
  const isEditing = editing !== null;
  const [brand, setBrand] = useState(editing?.brand ?? "");
  const [name, setName] = useState(editing?.name ?? "");
  const [ptype, setPtype] = useState(editing?.paint_type ?? "");
  const [color, setColor] = useState(editing?.color_family ?? "");
  const [qty, setQty] = useState(String(editing?.qty ?? "1"));
  // false = own it (on the shelf); true = on the shopping list (still to buy).
  const [wanted, setWanted] = useState<boolean>(!!editing?.wanted);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  async function save() {
    if (!name.trim()) return;
    setSaving(true); setErr(null);
    const q = Math.max(0, parseInt(qty || "1", 10) || 0);
    const w = wanted ? 1 : 0;
    try {
      if (isEditing) await store.updatePaint(editing!.id, { brand: brand.trim(), name: name.trim(), paint_type: ptype.trim(), color_family: color.trim(), qty: q, wanted: w });
      else await store.createPaint({ brand: brand.trim(), name: name.trim(), paint_type: ptype.trim(), color_family: color.trim(), qty: q, wanted: w });
      onSaved();
    } catch (e) { setErr(`Could not save — try again. (${e instanceof Error ? e.message : String(e)})`); setSaving(false); }
  }

  return (
    <ModalShell title={isEditing ? "Edit paint" : "Add paint"} onCancel={onClose} wide>
      <Field label="Paint name"><input placeholder="e.g. Macragge Blue" value={name} onChange={(e) => setName(e.target.value)} /></Field>
      <Field label="Brand (optional)"><input placeholder="e.g. Citadel" value={brand} onChange={(e) => setBrand(e.target.value)} /></Field>
      <div className="row" style={{ gap: "0.6rem", alignItems: "flex-start" }}>
        <div style={{ flex: 1 }}>
          <Field label="Type (optional)">
            <input list="paint-types" placeholder="base / layer / shade…" value={ptype} onChange={(e) => setPtype(e.target.value)} />
            <datalist id="paint-types">{PAINT_TYPES.map((t) => <option key={t} value={t} />)}</datalist>
          </Field>
        </div>
        <div style={{ flex: 1 }}><Field label="Qty"><input inputMode="numeric" placeholder="1" value={qty} onChange={(e) => setQty(e.target.value.replace(/[^0-9]/g, ""))} /></Field></div>
      </div>
      <Field label="Own it or need it?">
        <div style={{ display: "flex", gap: "0.35rem" }}>
          <button type="button" className={`chip${!wanted ? " selected" : ""}`} onClick={() => setWanted(false)}>
            <Palette size={13} /> I own this
          </button>
          <button type="button" className={`chip${wanted ? " selected" : ""}`} onClick={() => setWanted(true)}>
            <ShoppingCart size={13} /> Shopping list
          </button>
        </div>
      </Field>
      <Field label="Color family (optional)">
        <div style={{ display: "flex", flexWrap: "wrap", gap: "0.35rem" }}>
          {COLOR_FAMILIES.map((c) => (
            <button key={c} type="button" className={`chip${color === c ? " selected" : ""}`} onClick={() => setColor(color === c ? "" : c)}>
              <span className="status-dot" style={{ background: colorSwatch(c) }} /> {c}
            </button>
          ))}
        </div>
      </Field>
      {err && <div style={{ fontSize: "var(--fs-xs)", color: "var(--rose)", marginBottom: "0.75rem" }}>{err}</div>}
      <ModalActions onCancel={onClose} saving={saving} canSave={!!name.trim()} saveLabel={isEditing ? "Update" : "Add paint"} onSave={save} />
    </ModalShell>
  );
}

// ── Shared modal bits ────────────────────────────────────────────────────────────────
function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div style={{ marginBottom: "0.85rem" }}>
      <div style={{ fontWeight: 600, fontSize: "var(--fs-sm)", marginBottom: "0.4rem" }}>{label}</div>
      {children}
    </div>
  );
}

function ModalActions({ onCancel, saving, canSave, saveLabel, onSave }:
  { onCancel: () => void; saving: boolean; canSave: boolean; saveLabel: string; onSave: () => void }) {
  return (
    <div className="row" style={{ justifyContent: "flex-end", gap: "0.5rem", marginTop: "0.25rem" }}>
      <button className="btn" onClick={onCancel} disabled={saving}>Cancel</button>
      <button className="btn btn-primary" onClick={onSave} disabled={!canSave || saving}>{saving ? "Saving…" : saveLabel}</button>
    </div>
  );
}

function ModalShell({ title, children, onCancel, wide }: { title: string; children: React.ReactNode; onCancel: () => void; wide?: boolean }) {
  return (
    <div role="dialog" aria-modal="true" aria-label={title}
      onClick={onCancel}
      style={{ position: "fixed", inset: 0, background: "rgba(28,36,48,0.45)", display: "grid", placeItems: "center", zIndex: 50, padding: "1rem" }}>
      <div className="card" onClick={(e) => e.stopPropagation()}
        // FILL-THEN-CAP, not cap-then-fill. `width: 460` + `maxWidth: "100%"` resolves the 100%
        // against the centered grid track rather than the viewport, so on a 402pt phone the card
        // rendered at its full 460 and the right-hand controls sat off-screen (Round Card,
        // 2026-07-24). Setting width:100% and capping with maxWidth gives the same desktop size
        // while genuinely fitting a phone.
        style={{ width: "100%", maxWidth: wide ? 460 : 360, padding: "1.5rem", boxShadow: "var(--shadow-2)", borderRadius: "var(--radius-dialog)", maxHeight: "88vh", overflowY: "auto" }}>
        <div className="serif" style={{ fontWeight: 600, fontSize: "1.1rem", marginBottom: "1rem" }}>{title}</div>
        {children}
      </div>
    </div>
  );
}

// A small visual swatch for a color-family label (purely cosmetic).
function colorSwatch(family: string): string {
  const map: Record<string, string> = {
    red: "#c0524e", orange: "#d0843a", yellow: "#d8c24a", green: "#5b9d6a", blue: "#4f7fb0",
    purple: "#8a6fb0", brown: "#8a6a4a", flesh: "#caa07a", bone: "#d9cfb0", white: "#eef1f5",
    grey: "#9aa3af", black: "#3a4250", metallic: "#9aa1ab", gold: "#c4a14a", silver: "#c2c7cd",
  };
  return map[(family || "").toLowerCase()] ?? "#9aa3af";
}
